/**
 * /guides/wedding-qr-code-for-photos
 *
 * "Wedding QR Code for Photos: How Does It Work?" — the seventh production
 * guide, and the second in the photo cluster the sixth one opened.
 *
 * Its subject is one journey, and every section is a station on it: QR code,
 * scan, destination opens, guest chooses photos and videos, upload, the couple
 * receives the memories. A later edit that adds a section belonging to some
 * other journey is the thing to refuse.
 *
 * ── Deliberately NOT another photo-collection article ─────────────────────
 * `how-to-collect-wedding-photos-from-guests` is the guide that compares shared
 * albums, group chats, asking afterwards and a dedicated upload page. This one
 * assumes that decision is being made elsewhere and answers the narrower
 * question a couple asks after hearing the phrase "wedding QR code": what
 * actually happens when a guest scans it, and what has to be right on the day.
 * Nothing here may drift into a second method comparison. The one comparison
 * that does belong — QR code against a plain URL — earns its place because a QR
 * code IS a physical entry point to a URL, and it stays short.
 *
 * It is also not a QR-code technology tutorial. No generators, no encoding, no
 * error correction, no design tooling. The code is the simple half; the guest
 * experience and the wedding-day implementation are the half worth writing.
 *
 * ── The hedges, which are load-bearing ────────────────────────────────────
 * The whole article rests on one distinction, and it must survive editing: the
 * QR code carries an address and nothing else. Every behaviour a reader might
 * attribute to "the QR code" — accounts, permissions, what a guest sees, what
 * media is accepted, who can get in — belongs to the destination behind it and
 * differs between services. That is why the piece is useful to someone who
 * never uses Agapaé, and why sentences here say "depending on the service"
 * rather than describing one product's flow as how QR codes work.
 *
 * Banned outright, because they are the claims the category's own marketing
 * makes and none of them is ours: exact print dimensions, guaranteed scan
 * success, more uploads, "no app and no account" as universal behaviour,
 * unlimited uploads, permanent storage, automatic or live uploading, every
 * phone behaving identically, and QR codes being better than links rather than
 * more convenient in a room. Placement advice is offered as a principle with
 * examples, never as a ranking — no couple's venue was measured.
 *
 * ── The claims this guide is allowed to make ──────────────────────────────
 * The same six the sixth guide established, in the same closing section, and
 * nothing beyond them:
 *
 *   - "the guest gallery is part of every Agapaé plan, with different limits
 *     on each"  (the note)
 *     True at `api/constants/planLimits.mjs`: all three tiers carry a gallery
 *     with limits. NOT "photo and video gallery" — Blossom sold from 2026-10-14
 *     is photos only (`videoUploads: false`).
 *   - guests upload photos and videos to the wedding gallery (videos are
 *     plan-dependent, which the limits sentence says)
 *   - they reach it through the event's guest-facing upload flow
 *   - that flow opens from a QR code or a link  (the app's QR code page,
 *     pages/couple/qr-code/, renders the QR for the same shareable URL its
 *     copy button copies)
 *   - the gallery is private to the event rather than a public feed
 *   - the plan sets the limits: how many guests can upload, how much the
 *     gallery holds, how long the upload window runs, and whether guests can
 *     add videos
 *
 * Never: no account required, no app required, original quality on every plan,
 * permanent storage, automatic backup, live slideshows, upload notifications,
 * moderation, or any claim about how many guests will scan anything.
 *
 * ── Two external facts, both verified at the source ───────────────────────
 * In the privacy section, and cited as examples of why the destination matters
 * rather than as a comparison:
 *
 *   - Google Photos documents that an album can be shared by link and by QR
 *     code, and that anyone who scans it can access the shared album.
 *     support.google.com/photos/answer/6131416
 *   - Apple documents that Shared Albums let the people you invite add photos
 *     and videos, with "Subscribers Can Post" and "Public Website" as the
 *     owner's controls.  support.apple.com/en-us/108314
 *
 * Neither is described as better. If one of these pages changes, fix the
 * sentence rather than dropping the section: the point it makes is that "scan
 * this code" means different things behind different services.
 *
 * ── One thing to leave alone ──────────────────────────────────────────────
 * No copy in this file may contain "&", "<" or ">". The generator escapes them
 * correctly, but `guides.seo.test.mjs` compares against tag-stripped HTML
 * without decoding entities, so a string containing one is reported missing
 * from a document that in fact carries it.
 */

const weddingQrCodeForPhotosEn = {
  slug: "wedding-qr-code-for-photos",

  title: "Wedding QR Code for Photos: How Does It Work?",
  metaTitle: "Wedding QR Code for Photos: How Does It Work? | Agapaé",
  metaDescription:
    "Learn how a wedding QR code for photos works, where to place it, how guests upload photos, and what to check before your wedding.",

  eyebrow: "Wedding photos",

  hero: {
    src: "/assets/images/lovely_couple_003.jpeg",
    // Describes the photograph, not the keyword: a black and white reception
    // scene, the couple cutting their cake under strings of small lights, with
    // flowers in the foreground and other people moving behind them. It is the
    // room a table card actually sits in, and it is genuinely what is in the
    // frame — no phone and no printed code, because neither is visible here and
    // describing one would be a lie told to the readers who cannot see it.
    alt: "A black and white photograph of a couple cutting their wedding cake at a reception, strings of small lights above them, flowers on the table in the foreground and other people moving in the background",
    width: 1280,
    height: 714,
  },

  // Absent until the guide is genuinely published. Adding one means adding the
  // other, and `dateModified` may not precede `datePublished`.
  // datePublished: "YYYY-MM-DD",
  // dateModified: "YYYY-MM-DD",

  intro: [
    `By the end of the night your guests are carrying hundreds of photographs of your wedding. What they do not have is any idea where to send them.`,
    `That is the small, practical problem a wedding QR code solves. You cannot read a web address out to a room, and nobody wants to type one while holding a drink.`,
    `A wedding QR code for photos is a scannable shortcut to the place where your wedding photos are collected. A guest points a camera at it, and the destination opens.`,
    `The code itself is the simple part, and it is worth being clear about what it is not. A QR code is not the photo album. It is the bridge between a physical wedding setting and the digital place the photos go.`,
  ],

  sections: [
    {
      heading: "What Is a Wedding QR Code for Photos?",
      content: [
        `A QR code is a printed square pattern holding a piece of text, almost always a web address. A phone camera reads the pattern and offers to open it.`,
        `There is nothing wedding-specific about the code. What makes it a wedding photo QR code is the destination it points to, which might be:`,
        {
          type: "list",
          items: [
            `a shared album`,
            `a guest upload page`,
            `an event gallery`,
            `another photo-sharing destination`,
          ],
        },
        `They do not all behave the same way. What a guest sees, whether they need an account, and what happens to the files afterwards are decided by the service, not by the code.`,
        {
          type: "p",
          text: [
            `The destination is the bigger decision, and a separate one. If you have not made it yet, start with `,
            {
              text: "how to collect wedding photos from guests",
              href: "/guides/how-to-collect-wedding-photos-from-guests",
            },
            ` and come back to the code.`,
          ],
        },
      ],
    },
    {
      heading: "How Does a Wedding Photo QR Code Work?",
      content: [
        `End to end, it is a short sequence with only one step that belongs to the code itself.`,
        {
          type: "steps",
          items: [
            `You choose or create the place where the photos will be collected.`,
            `A QR code is generated for that destination's address.`,
            `The code is printed or displayed somewhere at your wedding.`,
            `A guest opens their phone camera and points it at the code.`,
            `The phone offers to open the address, and the destination loads.`,
            `The guest selects the photos, and any videos, they want to share.`,
            `The media is submitted to the album, gallery or upload page behind the code.`,
          ],
        },
        `Steps one to three are yours, before the wedding. Steps four to seven are the guest's, in a room with music playing.`,
        `Notice where the code's job ends. It carries an address; everything after the scan is the destination's work.`,
      ],
    },
    {
      heading: "What Happens When a Guest Scans It?",
      content: [
        `Most modern phones scan a QR code from the ordinary camera app: point it at the code, and a notification offers to open the address. Some older phones need a scanner app instead.`,
        `What loads next depends entirely on the service you chose: an upload page that is immediately ready, an album that asks the guest to join first, or a screen asking them to sign in or type a name.`,
        `Whatever the destination, the guest still has to:`,
        {
          type: "list",
          items: [
            `scan the code`,
            `open the destination that appears`,
            `choose which photos and videos to share`,
            `confirm, and wait for the upload to finish`,
          ],
        },
        `Their phone may also ask permission to reach the camera roll. That prompt comes from the phone rather than from you, and a guest who declines it can usually grant it later.`,
        `This is the part most articles skip. The QR code collects nothing by itself; it opens a page, and the experience is only as simple as whatever sits behind it.`,
      ],
    },
    {
      heading: "Where Should You Put a Wedding Photo QR Code?",
      content: [
        `Somewhere guests will notice it at a moment when they are likely to be taking or sharing photos. That is the whole principle, and it is worth more than any list. Places couples commonly use:`,
        {
          type: "list",
          items: [
            `the welcome sign, which guests are already reading`,
            `the entrance to the reception`,
            `dinner tables, on a card, the menu or beside the table number`,
            `the guest book area, where people already stop`,
            `the bar or a lounge corner, where phones tend to be out`,
            `wherever photographs are already being taken`,
          ],
        },
        `No single placement works best at every wedding: a garden reception, a long dinner and a late party give guests different paths through the evening. Two questions sort a good spot from a bad one. Will a guest be looking that way, and will their phone be in their hand?`,
        `A code in the corner of an order of service, or behind the flowers on a crowded table, is technically at the wedding and effectively invisible. Visibility does more of the work here than the code does.`,
      ],
    },
    {
      heading: "Should You Put the QR Code on Every Table?",
      content: [
        `It is a reasonable default for a seated dinner, and it is not a requirement.`,
        { type: "subheading", text: "What it gives you" },
        {
          type: "list",
          items: [
            `every guest has one within reach, without going to find it`,
            `nobody has to remember an address or ask where the photos go`,
            `it stays visible through the part of the evening that lasts longest`,
          ],
        },
        { type: "subheading", text: "What it costs you" },
        {
          type: "list",
          items: [
            `another printed item on a table you have styled carefully`,
            `it competes with the menu, the place cards and the flowers`,
            `it is redundant if you already have two or three obvious access points`,
          ],
        },
        `A practical rule: stand in the middle of the room and ask whether a seated guest could find the code without getting up. If yes, you have enough. If not, the tables are the simplest fix, and a small card the least intrusive one.`,
      ],
    },
    {
      heading: "How Should You Explain the QR Code to Guests?",
      content: [
        `A code with no words next to it is ambiguous. Your guests have scanned codes that turned out to be menus, playlists and gift lists, and nothing tells them which this one is.`,
        `One short line does the work:`,
        {
          type: "list",
          items: [
            `Scan to share your photos from today.`,
            `Have a photo from the day? Scan to add it to our wedding gallery.`,
          ],
        },
        `A guest should be able to tell, without stopping to think, what the code is for and roughly what happens after they scan it.`,
        `Keep it to a sentence: signage that explains an upload flow in five lines is read by nobody, and the destination can explain itself once it opens.`,
      ],
    },
    {
      heading: "Test the QR Code Before the Wedding",
      content: [
        `This is the part couples skip, and it decides whether the rest works. Before the wedding, check that:`,
        {
          type: "list",
          items: [
            `the code opens the destination you intended, not an old draft`,
            `the printed piece scans, not only the file on your screen`,
            `it works from more than one phone, ideally a newer and an older one`,
            `the destination makes sense to someone seeing it for the first time`,
            `selecting several photos at once behaves as you expect`,
            `a video uploads, if the destination takes video and you want it`,
            `access works for a guest who is not you`,
            `the printed code is clear and unobstructed where it will sit`,
          ],
        },
        `No universal print measurements are worth quoting: the right size depends on the paper, the printer and how far away a guest stands. The principles hold everywhere. Large enough to scan at arm's length, strong contrast, nothing resting on top of it, and checked under the light the room will have.`,
        `The best test costs ten minutes. Hand the printed card to someone who was not involved, ask them to share a photo, and watch without helping. Where they hesitate is where your guests will.`,
      ],
    },
    {
      heading: "QR Code or a Normal Link?",
      content: [
        `Both take a guest to the same kind of place, and often to exactly the same address.`,
        `In a room the QR code is the more convenient of the two: nothing to spell out across a table, nothing to type, nothing to get wrong. That is a physical advantage rather than a technical one.`,
        `A plain link is still the better tool for:`,
        {
          type: "list",
          items: [
            `messages sent before the wedding`,
            `a follow-up afterwards, when nobody is standing near your signage`,
            `a digital invitation or a wedding website`,
            `guests who already have the destination open on their phone`,
          ],
        },
        `Most weddings use both, which is fine as long as they point to the same destination. Two addresses collecting photos separately is the problem the whole exercise was meant to avoid.`,
      ],
    },
    {
      heading: "Can Guests Upload Videos Too?",
      content: [
        `They can, if the destination accepts video. The code has no opinion on it.`,
        `This is the distinction most often blurred. A QR code can point at a destination that takes photos and videos, but it does not decide what is accepted: formats, file sizes, length limits and the quality kept afterwards belong to the service.`,
        `Guest video is often the footage nobody else has: a speech in full, the dance floor from inside it. It is also the media most likely to meet a limit.`,
        `If video matters to you, test it with a real clip filmed on a phone rather than assuming that photo support implies video support.`,
      ],
    },
    {
      heading: "What About Privacy?",
      content: [
        `A QR code hands out access, printed and left on a table. Before yours goes anywhere, know:`,
        {
          type: "list",
          items: [
            `who can open the destination`,
            `whether guests can see what other people uploaded`,
            `whether the service controls access, or anyone with the address can enter`,
            `what happens if the code is passed on to someone who was not there`,
            `how long it stays open after the wedding, and what happens then`,
          ],
        },
        `Services answer these differently, which is the point. Google Photos documents that a shared album can be handed out as a link or as a QR code, that anyone who scans it can reach the album, and that the owner keeps control of the sharing. Apple Shared Albums let invited people add photos and videos, with the owner deciding whether subscribers can post and whether the album goes on a public website.`,
        `Neither is the right answer here. They are two documented examples of one lesson: "scan this code" means something different depending on what sits behind it. Decide which you want on purpose.`,
      ],
    },
    {
      heading: "A Simple Wedding QR Code Plan",
      content: [
        `Everything above fits on one page.`,
        { type: "subheading", text: "Before the wedding" },
        {
          type: "list",
          items: [
            `choose the destination where the photos will be collected`,
            `create the QR code for it, and test it on more than one phone`,
            `decide where it will appear, and in how many places`,
            `write one short line of instruction to sit beside it`,
          ],
        },
        { type: "subheading", text: "During the wedding" },
        {
          type: "list",
          items: [
            `place the code where guests can see it without looking for it`,
            `mention it briefly, if there is a natural moment`,
            `keep at least one obvious access point visible all evening`,
          ],
        },
        { type: "subheading", text: "After the wedding" },
        {
          type: "list",
          items: [
            `look through what your guests actually shared`,
            `keep the destination open for the photos that arrive late`,
            `download or preserve the media according to the storage and retention rules of whatever you chose`,
          ],
        },
        `Some guests will scan it, some will not, and a few will send photographs by message anyway. The plan makes sharing easy; it does not make it universal, and no wedding needs it to be.`,
      ],
    },
    {
      // The source outline closes with a separate Agapaé section, which is the
      // heading the `related` block below already renders as an H2. Two H2s
      // reading the same words is a defect, and all six earlier guides resolved
      // it the same way: the closing prose section is named for what it does,
      // and "Where this fits in Agapaé" stays with the related block.
      heading: "From QR Code to Shared Wedding Memories",
      content: [
        `The best wedding QR code is not the most elaborate one. It is the one that makes the next action obvious to a guest holding a phone.`,
        `On the day it should disappear into the evening: noticed, understood, used, forgotten. What you keep is not the code but the collection behind it, full of the moments your guests were closer to than anyone else.`,
        {
          // The only plan-availability claim in the guide, in the same place and
          // the same block type the six earlier guides put theirs.
          type: "note",
          text: `The guest gallery is part of every Agapaé plan, with different limits on each.`,
        },
        `In Agapaé, guests upload their photos and videos through the event's guest upload flow, which opens from a QR code or a link. The gallery is private to the event rather than a public feed.`,
        `The plan sets the limits: how many guests can upload, how much the gallery holds, how long the upload window stays open before and after the wedding, and whether guests can add videos as well as photos.`,
        {
          type: "p",
          text: [
            `For the product-specific experience, see `,
            { text: "wedding photo sharing", href: "/wedding-photo-sharing" },
            `.`,
          ],
        },
      ],
    },
  ],

  faqHeading: "Frequently Asked Questions",

  // Visible copy, always expanded. `faqSchema` is deliberately not set, exactly
  // as on the six earlier guides: Google restricts FAQ rich results to
  // government and health sites, so the markup would buy nothing while
  // committing us to a second copy of every answer.
  faq: [
    {
      q: `What is a wedding QR code for photos?`,
      a: `It is a printed code holding the web address of the place your wedding photos are collected. A guest points a phone camera at it and the destination opens. The code is only the shortcut: the album, gallery or upload page behind it is what receives the photos.`,
    },
    {
      q: `How do guests upload photos using a wedding QR code?`,
      a: `They scan the code, open the destination it offers, choose photos and videos from their camera roll, and confirm the upload. Their phone may ask permission to reach their photos first. The exact screens depend on the service behind the code, not on the code itself.`,
    },
    {
      q: `Where should I put a QR code for wedding photos?`,
      a: `Where guests will notice it while they are taking or sharing photos: a welcome sign, the entrance, dinner tables, the guest book area or the bar. No placement works best at every wedding, so pick the spots your guests genuinely pass, and keep at least one visible all evening.`,
    },
    {
      q: `Can a wedding QR code be used for videos too?`,
      a: `Yes, if the destination accepts video. The code carries an address and nothing more, so formats, file sizes, length limits and the quality kept afterwards are set by the service you chose. Guest videos are often speeches and dance-floor clips, so test a real one from a phone beforehand.`,
    },
    {
      q: `What should I test before using a wedding photo QR code?`,
      a: `Scan the printed version, not just the file on your screen, and check that it opens the destination you intended. Try it on more than one phone, upload a photo and a video, confirm that access works for someone who is not you, and make sure the printed code is clear and unobstructed where it will sit.`,
    },
  ],

  related: {
    heading: "Where this fits in Agapaé",

    // The first guide with a related guide to name: the photo cluster's opener,
    // which this article hands the destination decision back to. Registered, so
    // the link resolves rather than shipping a 404.
    guides: ["how-to-collect-wedding-photos-from-guests"],

    product: [
      {
        href: "/wedding-photo-sharing",
        label: "Wedding photo sharing",
        blurb:
          "See how guests upload photos and videos to a wedding gallery that is private to the event, reached from a QR code or a link. The gallery is part of every Agapaé plan, with different limits on each.",
      },
    ],
  },
};

export default weddingQrCodeForPhotosEn;
